import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { styled } from '@compiled/react';
import { withExperience } from '@helpcenter/analytics-components/src/experience';
import {
    ExperienceFailure as NotifyExperienceFailure,
    ExperienceSuccess as NotifyExperienceSuccess,
} from '@helpcenter/analytics-components/src/experience-notifier';
import { EngagementMessages, ExperienceName, HTTP, ScreenName } from '@helpcenter/constants';
import { DocumentTitle } from '@helpcenter/document-title';
import { EngagementCoordinationClient } from '@helpcenter/engagement-coordination-client';
import {
    isCustomPagesUiEnabled,
    isElementRendererSplitEnabled,
    isIsomorphicErrorHandlingEnabled,
    isRedirectToLoginPageOnInvalidTokenForHomePage,
    isUseHelpCenterRefresh,
    isVisualRefreshChangeboardingEnabled,
} from '@helpcenter/feature-flags';
import { FlexibleHomepageLoader } from '@helpcenter/flexible-homepage-loader';
import { editingHelpCenter } from '@helpcenter/help-center-preview';
import { getCloudId, getHelpCenterAri, isValidLayoutAriPresent } from '@helpcenter/meta';
import { sizes } from '@helpcenter/styles/src/sizes-viewport';
import ViewHomepageResource from '@helpcenter/view-homepage';
import { useIntl } from 'react-intl';
import { LazySuspense } from 'react-loosely-lazy';
import { di } from 'react-magnetic-di';
import { graphql, usePreloadedQuery } from 'react-relay';
import { Redirect } from 'react-resource-router';
import type { State } from 'state';
import { isCustomizing as getIsCustomizing } from 'state/selectors/help-center-preview';
import { getShown as getSearchShown } from 'state/selectors/search';
import { getCanAdministerJIRA, getUserState } from 'state/selectors/user';
import { LazyPushingCustomizeSidebar, useCustomiseSidebarTriggerRef } from 'view/common/pushing-customize-sidebar';
import { HEADER_HEIGHT } from 'view/common/styled';
import { VisualRefreshChangeboarding } from 'view/common/visual-refresh-changeboarding';
import { useMediaQuery } from 'view/help-center/use-screen-size/use-screen-size';
import { HelpCenterCustomizeSidebar } from 'view/help-center-customize-sidebar';
import { LazyHelpCenterBucketsRoutes } from 'view/help-center-customize-sidebar/bucket-routes';
import { FOOTER_HEIGHT } from 'view/layout/footer/styled';
import { LayoutBuilderContainer } from 'view/layout-next/controllers/store';
import { GenericErrorScreen } from 'view/layout-next/generic-error-screen';
import { LayoutViewRenderer } from 'view/layout-next/layout-view-renderer';
import { LayoutNextErrorBoundary } from 'view/layout-primitives/layout-next-error-boundary';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { SpotlightManager } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { FetchError } from '@atlassian/help-center-common-util/fetch/fetch';
import { useRelayResource } from '@atlassian/help-center-common-util/resource-router';
import { getTraceIdFromRelayQueryReference } from '@atlassian/help-center-common-util/retrieve-relay-traceid';
import { clearSidebarExperienceAndCustomize } from '@atlassian/help-center-common-util/sidebar-experience';
import { SidebarToggle } from '@atlassian/help-center-common-util/sidebar-toggle';
import { getLoginUrl } from '@atlassian/help-center-common-util/url';
import { HomePageElementViewRenderer } from '@atlassian/help-center-home-page-element-view-renderer';
import { helpCenterTopicsProductExperience } from '@atlassian/list-of-topics-element';
import { RovoServiceAgentWidgetInjector } from '@atlassian/rovo-service-agent-chat-widget';
import { useUFOComponentExperience } from '@atlassian/ufo';
import type { viewHomepageQuery } from './__generated__/viewHomepageQuery.graphql';
import { viewHomepageExperience } from './experiences';
import messages from './messages';
import type { ViewHomepageProps } from './types';

const ViewHomepage = ({
    queryReference,
    isCustomizing,
    searchBarShown,
    onStartEditing,
    onFinishEditing,
    canAdministerJira,
}: ViewHomepageProps) => {
    di(
        FireScreenEventOnMount,
        DocumentTitle,
        LazyPushingCustomizeSidebar,
        SidebarToggle,
        isValidLayoutAriPresent,
        usePreloadedQuery,
        Redirect,
        getLoginUrl
    );
    const intl = useIntl();
    const isSmallScreenSize = useMediaQuery(`(max-width: ${sizes.small}px)`);
    const customiseSidebarTriggerRef = useCustomiseSidebarTriggerRef();

    if (!isValidLayoutAriPresent()) {
        // this is the initial docs call traceId we get from Jira
        const traceId = getEnv().traceId ?? '';
        throw new Error(`Invalid layout ari. EnvTraceId: ${traceId}`);
    }

    const data = usePreloadedQuery<viewHomepageQuery>(
        graphql`
            query viewHomepageQuery($helpCenterAri: ID!, $cloudId: ID!, $layoutAri: ID!, $filter: HelpLayoutFilter) {
                helpCenter(cloudId: $cloudId) {
                    helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: ["HelpCenterAggBeta"]) {
                        ... on HelpCenter {
                            name {
                                default
                            }
                            announcements {
                                canEditHomePageAnnouncement
                            }
                        }
                        ... on QueryError {
                            __typename
                        }
                    }
                }
                helpLayout(cloudId: $cloudId) @optIn(to: ["HelpLayoutExperimentalSchema"]) {
                    layout(id: $layoutAri, filter: $filter) @required(action: THROW) {
                        ... on HelpLayout {
                            ...layoutViewRendererFragment
                        }
                        ... on QueryError {
                            extensions {
                                statusCode
                            }
                            message
                            __typename
                        }
                    }
                }
            }
        `,
        queryReference
    );

    const onOpen = useCallback(() => {
        onStartEditing();
    }, [onStartEditing]);

    const handleFinishEditing = useCallback(() => {
        clearSidebarExperienceAndCustomize();
        onFinishEditing();
    }, [onFinishEditing]);

    useEffect(() => {
        // We are marking the experience as success once the component mounts successfully. We are not using ExperienceSuccess in render because if  there is a error in useEffect, the experience will be wrongly marked as success even before hitting the error boundary.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        viewHomepageExperience.success();
    }, []);

    if (data?.helpLayout?.layout?.__typename === 'QueryError') {
        const extensions = data?.helpLayout?.layout.extensions;
        const message = data?.helpLayout?.layout?.message || '';
        let statusCode = HTTP.SERVER;
        if (extensions && Array.isArray(extensions) && extensions.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            statusCode = extensions[0].statusCode;
        }
        if (isIsomorphicErrorHandlingEnabled() && __SERVER__) {
            return (
                <Box xcss={errorScreenWrapper}>
                    <AnalyticsContext data={{ attributes: {} }}>
                        <GenericErrorScreen />
                    </AnalyticsContext>
                </Box>
            );
        }
        throw new FetchError(message, statusCode, true);
    }

    if (!data?.helpLayout?.layout) {
        return null;
    }

    const helpCenterName = data?.helpCenter?.helpCenterById?.name?.default;
    const title = intl.formatMessage(messages.helpCenterTitle, { helpCenterTitle: helpCenterName });
    const showCustomizeSidebar = Boolean(data?.helpCenter?.helpCenterById?.announcements?.canEditHomePageAnnouncement);
    return (
        <DocumentTitle title={title} ignoreBaseTitle>
            <SpotlightManager>
                <LayoutViewerWrapper>
                    <LayoutViewRenderer layout={data?.helpLayout?.layout} />
                    {showCustomizeSidebar && (
                        <LazySuspense fallback={null}>
                            {!searchBarShown ? (
                                <LazyPushingCustomizeSidebar
                                    isOpen={isCustomizing}
                                    onOpen={onOpen}
                                    onClose={handleFinishEditing}
                                    triggerRef={customiseSidebarTriggerRef}
                                >
                                    {!isSmallScreenSize ? (
                                        <HelpCenterBucketsContainer>
                                            <LazyHelpCenterBucketsRoutes
                                                isOpen={isCustomizing}
                                                onClose={handleFinishEditing}
                                            />
                                        </HelpCenterBucketsContainer>
                                    ) : (
                                        <HelpCenterCustomizeSidebar
                                            isOpen={isCustomizing}
                                            onClose={handleFinishEditing}
                                        />
                                    )}
                                </LazyPushingCustomizeSidebar>
                            ) : null}
                        </LazySuspense>
                    )}
                    {isUseHelpCenterRefresh() && isVisualRefreshChangeboardingEnabled() && canAdministerJira && (
                        <EngagementCoordinationClient messageId={EngagementMessages.CHANGE_BOARD_VISUAL_REFRESH}>
                            <VisualRefreshChangeboarding />
                        </EngagementCoordinationClient>
                    )}
                    <SidebarToggle openSidebar={onOpen} closeSidebar={handleFinishEditing} />
                    <FireScreenEventOnMount />
                    <NotifyExperienceSuccess location="view-home-page" />
                    {FeatureGates.checkGate('rovo_service_agent_help_center_widget_experience') && (
                        <RovoServiceAgentWidgetInjector
                            surface="HELP_CENTER"
                            siteId={getCloudId()}
                            widgetId={getHelpCenterAri()}
                        />
                    )}
                </LayoutViewerWrapper>
            </SpotlightManager>
        </DocumentTitle>
    );
};

const selector = createSelector(
    [getCanAdministerJIRA, getSearchShown, getIsCustomizing],
    (canAdministerJira, searchBarShown, isCustomizing) => ({
        canAdministerJira,
        searchBarShown,
        isCustomizing,
    })
);
const ConnectedViewHomepage = connect(selector, {
    onStartEditing: () => editingHelpCenter(true),
    onFinishEditing: () => editingHelpCenter(false),
})(ViewHomepage);

export interface Props {
    user: {
        isLoggedIn: boolean;
    };
    isLoading: boolean;
}
const ViewHomePageLoader = ({ isLoading, user }: Props) => {
    di(
        useRelayResource,
        NotifyExperienceFailure,
        Redirect,
        getLoginUrl,
        isRedirectToLoginPageOnInvalidTokenForHomePage
    );
    const { queryReference } = useRelayResource(ViewHomepageResource);
    useUFOComponentExperience(viewHomepageExperience);
    useUFOComponentExperience(helpCenterTopicsProductExperience);

    const loginUrl = getLoginUrl({
        redirectUrl: '/portals',
    });

    // TODO: Move this to a lazy element error handler  to exclude from the main bundle
    const renderError = useCallback(
        (error: Error | FetchError) => {
            const traceId = getTraceIdFromRelayQueryReference(queryReference);
            const analyticsAttributes = {
                traceId,
                screenName: ScreenName.VIEW_HOMEPAGE,
                errorMessage: error.message,
                status: (error as FetchError).status,
            };

            if (
                isRedirectToLoginPageOnInvalidTokenForHomePage() &&
                (error as FetchError).status === HTTP.UNAUTHORIZED
            ) {
                // When redirecting to login page we need to make sure user is not already logged in else it will create a redirect loop
                if (!isLoading && !user.isLoggedIn) {
                    return <Redirect push to={loginUrl} />;
                }
            }

            if (!isFetchNetworkOrClientError(error as FetchError)) {
                return (
                    <NotifyExperienceFailure
                        location="view-home-page"
                        errorMessage={error.message}
                        traceId={traceId}
                        status={(error as FetchError).status}
                    >
                        <Box xcss={errorScreenWrapper}>
                            <AnalyticsContext data={{ attributes: analyticsAttributes }}>
                                <GenericErrorScreen />
                            </AnalyticsContext>
                        </Box>
                    </NotifyExperienceFailure>
                );
            }

            return (
                <Box xcss={errorScreenWrapper}>
                    <AnalyticsContext data={{ attributes: analyticsAttributes }}>
                        <GenericErrorScreen />
                    </AnalyticsContext>
                </Box>
            );
        },
        [loginUrl, queryReference, user, isLoading]
    );
    if (!queryReference) return <FlexibleHomepageLoader />;

    return (
        <LayoutNextErrorBoundary id="root" renderError={renderError} screenName={ScreenName.VIEW_HOMEPAGE}>
            <>
                <LazySuspense fallback={<FlexibleHomepageLoader />}>
                    <LayoutBuilderContainer
                        ElementRenderer={isElementRendererSplitEnabled() ? HomePageElementViewRenderer : undefined}
                        screenName={isCustomPagesUiEnabled() ? ScreenName.VIEW_HOMEPAGE : undefined}
                        traceId={
                            isCustomPagesUiEnabled() ? getTraceIdFromRelayQueryReference(queryReference) : undefined
                        }
                        experience={isCustomPagesUiEnabled() ? viewHomepageExperience : undefined}
                    >
                        <ConnectedViewHomepage queryReference={queryReference} />
                    </LayoutBuilderContainer>
                </LazySuspense>
            </>
        </LayoutNextErrorBoundary>
    );
};

const homePageLoaderSelector = createSelector(
    (state: State) => getUserState(state),
    (userState) => ({
        user: {
            isLoggedIn: userState.isLoggedIn,
        },
        isLoading: !userState.isLoaded,
    })
);
export default connect(homePageLoaderSelector, {})(withExperience(ExperienceName.HELP_CENTER)(ViewHomePageLoader));

// Added this to remove header and footer height and take the complete height for showing error
const errorScreenWrapper = xcss({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
    height: `calc(100vh - ${HEADER_HEIGHT + FOOTER_HEIGHT}px)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LayoutViewerWrapper = styled.div({
    position: 'relative',
    paddingBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpCenterBucketsContainer = styled.div({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
});
